import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserService } from '@/services/users/UserService';

interface SetupAnalysisCompletePayload {
  userId: string;
  conversationId: string;
  genieName: string;
  timestamp: string;
}

export const useSetupRetryNotifications = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let currentUserId: string | null = null;
    let subscriptionCleanup: (() => void) | null = null;

    const setupNotificationListener = async () => {
      try {
        const { data: { user }, error: userError } = await UserService.getUser();

        if (userError || !user) {
          console.warn('Unable to get current user for setup notifications:', userError);
          return;
        }

        currentUserId = user.id || null;

        if (!currentUserId) {
          console.warn('No user ID available for setup notifications');
          return;
        }

        const channel = supabase.channel('setup-notifications');

        channel
          .on(
            'broadcast',
            { event: 'setup-analysis-complete' },
            ({ payload }: { payload: SetupAnalysisCompletePayload }) => {
              console.log('Received setup-analysis-complete event:', payload);

              // Only show notification if it's for the current user
              if (currentUserId && payload.userId === currentUserId) {
                toast.success(
                  `Your genie "${payload.genieName}" from earlier conversation has been analyzed!`,
                  {
                    description: 'Review the setup in the unfinished setups section and create your genie.',
                    duration: 10000,
                    action: {
                      label: 'View',
                      onClick: () => {
                        window.location.href = '/user-hub/my-genies';
                      },
                    },
                  }
                );

                // Invalidate queries to refresh unfinished setups list
                queryClient.invalidateQueries({ queryKey: ['unfinished-setups'] });
              }
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log('Subscribed to setup-notifications channel');
            } else if (status === 'CLOSED') {
              console.log('Unsubscribed from setup-notifications channel');
            }
          });

        subscriptionCleanup = () => {
          channel.unsubscribe();
        };
      } catch (error) {
        console.error('Error setting up notification listener:', error);
      }
    };

    setupNotificationListener();

    return () => {
      if (subscriptionCleanup) {
        subscriptionCleanup();
      }
    };
  }, [queryClient]);
};
